import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, deleteDoc, doc, getDocs, runTransaction, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import { useAuth } from '../context/AuthContext';
import { getUserProfile } from '../services/profilesService';
import './Home.css';

const TEMPLATE_COLLECTION = 'emailTemplates';
const COUNTER_COLLECTION = 'counters';
const EMAIL_TEMPLATE_COUNTER_ID = 'emailTemplates';
const SUBJECT_MAX_LENGTH = 80;
const DEFAULT_BATCH_SIZE = 5;

const createEmptyForm = () => ({
  id: '',
  number: '',
  name: '',
  fromEmail: 'no_reply@icnacon.org',
  batchSize: String(DEFAULT_BATCH_SIZE),
  subject: '',
  type: 'dynamic',
  htmlContent: '',
  variablesText: '',
});

const serializeTemplateForm = (form) => JSON.stringify({
  id: String(form?.id || ''),
  number: String(form?.number || ''),
  name: String(form?.name || ''),
  fromEmail: String(form?.fromEmail || 'no_reply@icnacon.org'),
  batchSize: String(form?.batchSize || DEFAULT_BATCH_SIZE),
  subject: String(form?.subject || ''),
  type: String(form?.type || 'dynamic'),
  htmlContent: String(form?.htmlContent || ''),
  variablesText: String(form?.variablesText || ''),
});

const normalizeVariableName = (value) => String(value || '').replace(/[{}]/g, '').trim();

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parseVariablesInput = (value) => {
  if (!value) return [];
  const seen = new Set();
  return String(value)
    .split(/[\n,]/)
    .map((item) => normalizeVariableName(item))
    .filter((item) => {
      if (!item || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
};

const parseRecipientEmailsInput = (value) => {
  if (!value) return [];
  const seen = new Set();
  return String(value)
    .split(/[;,]/)
    .map((item) => String(item || '').trim())
    .filter((item) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const listsMatchAsSet = (left, right) => {
  const leftList = Array.isArray(left) ? left : [];
  const rightList = Array.isArray(right) ? right : [];
  if (leftList.length !== rightList.length) return false;
  const rightSet = new Set(rightList);
  return leftList.every((item) => rightSet.has(item));
};

const extractVariablesFromHtml = (htmlContent) => {
  if (!htmlContent) return [];
  const matches = String(htmlContent).matchAll(/{{\s*([a-zA-Z0-9_.-]+)\s*}}/g);
  const seen = new Set();
  const variables = [];
  for (const match of matches) {
    const normalized = normalizeVariableName(match[1]);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    variables.push(normalized);
  }
  return variables;
};

const buildVariableText = (variables) => Array.isArray(variables) && variables.length ? variables.join('\n') : '';

const parsePositiveInteger = (value) => {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const toMillis = (value) => {
  try {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (value.seconds !== undefined && value.seconds !== null) {
      return (Number(value.seconds) * 1000) + Math.floor(Number(value.nanoseconds || 0) / 1000000);
    }
    const dt = new Date(value);
    const ms = dt.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  } catch {
    return 0;
  }
};

const formatDateTime = (value) => {
  const ms = toMillis(value);
  if (!ms) return '-';
  return new Date(ms).toLocaleString();
};

const formatTemplateNumber = (value) => String(Math.max(0, Number(value) || 0)).padStart(4, '0');

const reserveNextTemplateNumber = async () => {
  const counterRef = doc(db, COUNTER_COLLECTION, EMAIL_TEMPLATE_COUNTER_ID);
  return runTransaction(db, async (transaction) => {
    const counterSnapshot = await transaction.get(counterRef);
    const currentValue = counterSnapshot.exists() ? Number(counterSnapshot.data()?.latestValue || 0) : 0;
    const normalizedCurrentValue = Number.isFinite(currentValue) && currentValue > 0 ? Math.floor(currentValue) : 0;
    const nextValue = normalizedCurrentValue + 1;

    transaction.set(counterRef, {
      latestValue: nextValue,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    return formatTemplateNumber(nextValue);
  });
};

const EmailTemplatesAdmin = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingNumber, setGeneratingNumber] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showPreviewValuesModal, setShowPreviewValuesModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewSourceSubject, setPreviewSourceSubject] = useState('');
  const [previewSourceHtml, setPreviewSourceHtml] = useState('');
  const [previewTemplateNumber, setPreviewTemplateNumber] = useState('');
  const [previewTemplateType, setPreviewTemplateType] = useState('static');
  const [previewTemplateVariables, setPreviewTemplateVariables] = useState([]);
  const [previewVariableNames, setPreviewVariableNames] = useState([]);
  const [previewVariableValues, setPreviewVariableValues] = useState({});
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [fontColor, setFontColor] = useState('#000000');
  const [isEditingHtmlCode, setIsEditingHtmlCode] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState(createEmptyForm());
  const [savedFormSignature, setSavedFormSignature] = useState('');
  const htmlEditorRef = useRef(null);
  const templateNameInputRef = useRef(null);

  const role = String(userProfile?.userType || '').trim();
  const canManageTemplates = role === 'Admin' || role === 'SystemAdmin' || role === 'EventAdmin';

  const currentUserName = useMemo(() => {
    const fullName = `${String(userProfile?.firstName || '').trim()} ${String(userProfile?.lastName || '').trim()}`.trim();
    return fullName || currentUser?.email || currentUser?.uid || 'Unknown User';
  }, [currentUser?.email, currentUser?.uid, userProfile?.firstName, userProfile?.lastName]);

  const detectedVariablesInContent = useMemo(
    () => extractVariablesFromHtml(templateForm.htmlContent),
    [templateForm.htmlContent]
  );

  const detectedVariablesInSubject = useMemo(
    () => extractVariablesFromHtml(templateForm.subject),
    [templateForm.subject]
  );

  const detectedVariables = useMemo(() => {
    const seen = new Set();
    return [...detectedVariablesInSubject, ...detectedVariablesInContent].filter((item) => {
      if (!item || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
  }, [detectedVariablesInSubject, detectedVariablesInContent]);

  const manualVariables = useMemo(
    () => parseVariablesInput(templateForm.variablesText),
    [templateForm.variablesText]
  );

  const effectiveVariables = useMemo(() => {
    if (templateForm.type === 'static') return [];
    const seen = new Set();
    return [...manualVariables, ...detectedVariables].filter((item) => {
      if (!item || seen.has(item)) return false;
      seen.add(item);
      return true;
    });
  }, [templateForm.type, manualVariables, detectedVariables]);

  const placeholderChips = useMemo(
    () => (templateForm.type === 'dynamic' ? effectiveVariables : []),
    [templateForm.type, effectiveVariables]
  );

  const fetchTemplates = async () => {
    const snapshot = await getDocs(collection(db, TEMPLATE_COLLECTION));
    const rows = snapshot.docs.map((templateDoc) => {
      const data = templateDoc.data() || {};
      return {
        docId: templateDoc.id,
        id: data.id || templateDoc.id,
        number: data.number || '',
        name: data.name || '',
        fromEmail: data.fromEmail || 'no_reply@icnacon.org',
        batchSize: String(data.batchSize || DEFAULT_BATCH_SIZE),
        subject: data.subject || '',
        type: data.type || 'static',
        htmlContent: data.htmlContent || '',
        variables: Array.isArray(data.variables) ? data.variables : [],
        createdBy: data.createdBy || '',
        createdByName: data.createdByName || '',
        createdAt: data.createdAt || null,
        updatedBy: data.updatedBy || '',
        updatedByName: data.updatedByName || '',
        updatedAt: data.updatedAt || null,
      };
    });

    rows.sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt));
    setTemplates(rows);
  };

  const loadPage = async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const profile = await getUserProfile(currentUser.uid);
      setUserProfile(profile || null);
    } catch {
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  useEffect(() => {
    if (loading) return;
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!canManageTemplates) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        setError('');
        await fetchTemplates();
      } catch (err) {
        const code = String(err?.code || '').toLowerCase();
        const message = String(err?.message || 'Failed to load email templates.');
        const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
        setError(isPermissionError
          ? 'Missing or insufficient permissions while reading email templates. Ensure Firestore rules allow Admin/SystemAdmin/EventAdmin users to read this collection.'
          : message);
      }
    })();
  }, [loading, currentUser, canManageTemplates, navigate]);

  const openCreateTemplateModal = async () => {
    setEditingTemplate(null);
    setTemplateForm({ ...createEmptyForm(), number: '' });
    setSavedFormSignature('');
    setShowTemplateModal(true);
    setIsEditingHtmlCode(false);
    setError('');
    setSuccess('');

    setGeneratingNumber(true);
    try {
      const nextTemplateNumber = await reserveNextTemplateNumber();
      const nextForm = { ...createEmptyForm(), number: nextTemplateNumber };
      setTemplateForm(nextForm);
      setSavedFormSignature(serializeTemplateForm(nextForm));
    } catch (err) {
      const code = String(err?.code || '').toLowerCase();
      const message = String(err?.message || 'Failed to generate template number.');
      const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
      setError(isPermissionError
        ? 'Missing or insufficient permissions while generating template number. Ensure Firestore rules allow Admin/SystemAdmin/EventAdmin users to read/write counters/emailTemplates.'
        : message);
    } finally {
      setGeneratingNumber(false);
    }
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate(template);
    const nextForm = {
      id: String(template?.id || template?.docId || ''),
      number: String(template?.number || ''),
      name: String(template?.name || ''),
      fromEmail: String(template?.fromEmail || 'no_reply@icnacon.org'),
      batchSize: String(template?.batchSize || DEFAULT_BATCH_SIZE),
      subject: String(template?.subject || ''),
      type: String(template?.type || 'static'),
      htmlContent: String(template?.htmlContent || ''),
      variablesText: buildVariableText(template?.variables),
    };
    setTemplateForm(nextForm);
    setSavedFormSignature(serializeTemplateForm(nextForm));
    setShowTemplateModal(true);
    setIsEditingHtmlCode(false);
    setError('');
    setSuccess('');
  };

  const isTemplateDirty = useMemo(
    () => showTemplateModal && savedFormSignature && serializeTemplateForm(templateForm) !== savedFormSignature,
    [showTemplateModal, savedFormSignature, templateForm]
  );

  const closeTemplateModal = async () => {
    if (saving || generatingNumber) return;

    if (isTemplateDirty) {
      const shouldSave = window.confirm('You have unsaved changes. Click OK to save before closing, or Cancel to close without saving.');
      if (shouldSave) {
        const saved = await handleSaveTemplate();
        if (!saved) return;
      }
    }

    setShowTemplateModal(false);
    setShowPreviewModal(false);
    setShowPreviewValuesModal(false);
    setIsEditingHtmlCode(false);
    setPreviewHtml('');
    setPreviewSubject('');
    setPreviewSourceSubject('');
    setPreviewSourceHtml('');
    setPreviewTemplateNumber('');
    setPreviewTemplateType('static');
    setPreviewTemplateVariables([]);
    setPreviewVariableValues({});
    setSavedFormSignature('');
  };

  const handleFormChange = (field, value) => {
    setTemplateForm((prev) => {
      if (field === 'type' && value === 'static') {
        return { ...prev, type: value, variablesText: '' };
      }
      if (field === 'subject') {
        return { ...prev, subject: String(value || '').slice(0, SUBJECT_MAX_LENGTH) };
      }
      return { ...prev, [field]: value };
    });
  };

  useEffect(() => {
    if (!showTemplateModal || !htmlEditorRef.current) return;
    const nextHtml = String(templateForm.htmlContent || '');
    if (htmlEditorRef.current.innerHTML !== nextHtml) {
      htmlEditorRef.current.innerHTML = nextHtml;
    }
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch {
      // Some browsers may not support this command; ignore silently.
    }
  }, [showTemplateModal, templateForm.htmlContent]);

  useEffect(() => {
    if (!showTemplateModal || editingTemplate || !templateNameInputRef.current) return;
    templateNameInputRef.current.focus();
  }, [showTemplateModal, editingTemplate]);

  const syncHtmlFromEditor = () => {
    const html = String(htmlEditorRef.current?.innerHTML || '');
    handleFormChange('htmlContent', html);
  };

  const updateActiveFormats = () => {
    if (!htmlEditorRef.current) return;
    try {
      setActiveFormats({
        bold: !!document.queryCommandState('bold'),
        italic: !!document.queryCommandState('italic'),
        underline: !!document.queryCommandState('underline'),
      });
    } catch {
      // Some commands may not be available in all browsers.
    }
  };

  useEffect(() => {
    if (!showTemplateModal) return;
    const handleSelectionChange = () => updateActiveFormats();
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [showTemplateModal]);

  const runEditorCommand = (command, value = null) => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;
    htmlEditorRef.current.focus();
    document.execCommand(command, false, value);
    syncHtmlFromEditor();
    updateActiveFormats();
  };

  const normalizeEditorLinks = () => {
    if (!htmlEditorRef.current) return;
    const links = htmlEditorRef.current.querySelectorAll('a');
    links.forEach((anchor) => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });
  };

  const clearFormatting = () => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;
    const editor = htmlEditorRef.current;
    editor.focus();

    const selection = window.getSelection ? window.getSelection() : null;
    const hasSelection = !!selection && selection.rangeCount > 0 && !selection.getRangeAt(0).collapsed;

    const toPlainText = (htmlFragment) => {
      const container = document.createElement('div');
      container.appendChild(htmlFragment);
      return container.innerText || container.textContent || '';
    };

    if (hasSelection) {
      const range = selection.getRangeAt(0);
      const plainText = toPlainText(range.cloneContents());
      range.deleteContents();
      const textNode = document.createTextNode(plainText);
      range.insertNode(textNode);

      const updatedRange = document.createRange();
      updatedRange.selectNodeContents(textNode);
      selection.removeAllRanges();
      selection.addRange(updatedRange);
      syncHtmlFromEditor();
      return;
    }

    const plainEditorText = editor.innerText || editor.textContent || '';
    editor.textContent = plainEditorText;

    if (selection) {
      selection.removeAllRanges();
      const caretRange = document.createRange();
      caretRange.selectNodeContents(editor);
      caretRange.collapse(false);
      selection.addRange(caretRange);
    }

    syncHtmlFromEditor();
  };

  const adjustSelectedTextSize = (direction = 'up') => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;
    const selection = window.getSelection ? window.getSelection() : null;
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) {
      showSaveError('Select text first, then change font size.');
      return;
    }

    const containerElement = range.startContainer?.nodeType === 1
      ? range.startContainer
      : range.startContainer?.parentElement;
    const computed = window.getComputedStyle(containerElement || htmlEditorRef.current);
    const currentFontSize = Number.parseFloat(computed.fontSize || '16') || 16;
    const parsedLineHeight = Number.parseFloat(computed.lineHeight || '');
    const currentLineHeight = Number.isFinite(parsedLineHeight) ? parsedLineHeight : (currentFontSize * 1.4);
    const step = direction === 'down' ? -1 : 1;
    const currentFontRounded = Math.round(currentFontSize);
    const nextFontSize = Math.max(10, currentFontRounded + step);
    const lineHeightRatio = currentFontSize > 0 ? (currentLineHeight / currentFontSize) : 1.4;
    const nextLineHeight = Math.max(12, Math.round(nextFontSize * lineHeightRatio));

    const wrapper = document.createElement('span');
    wrapper.style.fontSize = `${nextFontSize}px`;
    wrapper.style.lineHeight = `${nextLineHeight}px`;
    try {
      const fragment = range.extractContents();
      wrapper.appendChild(fragment);
      range.insertNode(wrapper);

      const updatedRange = document.createRange();
      updatedRange.selectNodeContents(wrapper);
      selection.removeAllRanges();
      selection.addRange(updatedRange);
    } catch {
      return;
    }

    syncHtmlFromEditor();
  };

  const insertParagraph = () => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;
    htmlEditorRef.current.focus();
    document.execCommand('insertParagraph', false);
    syncHtmlFromEditor();
  };

  const applyListFormat = (listType = 'ul') => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;
    const selection = window.getSelection ? window.getSelection() : null;
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = String(range.toString() || '').trim();
    const items = (selectedText || 'List item')
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);

    const list = document.createElement(listType === 'ol' ? 'ol' : 'ul');
    items.forEach((itemText) => {
      const li = document.createElement('li');
      li.textContent = itemText;
      list.appendChild(li);
    });

    range.deleteContents();
    range.insertNode(list);

    const updatedRange = document.createRange();
    updatedRange.selectNodeContents(list);
    selection.removeAllRanges();
    selection.addRange(updatedRange);

    syncHtmlFromEditor();
    updateActiveFormats();
  };

  const handleEditorKeyDown = (event) => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      insertParagraph();
    }
  };

  const handleApplyHtmlFormat = (formatType, value = null) => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;

    switch (formatType) {
      case 'bold':
        runEditorCommand('bold');
        break;
      case 'italic':
        runEditorCommand('italic');
        break;
      case 'underline':
        runEditorCommand('underline');
        break;
      case 'sizeUp':
        adjustSelectedTextSize('up');
        break;
      case 'sizeDown':
        adjustSelectedTextSize('down');
        break;
      case 'paragraph':
        insertParagraph();
        break;
      case 'ul':
        applyListFormat('ul');
        break;
      case 'ol':
        applyListFormat('ol');
        break;
      case 'alignLeft':
        runEditorCommand('justifyLeft');
        break;
      case 'alignCenter':
        runEditorCommand('justifyCenter');
        break;
      case 'alignRight':
        runEditorCommand('justifyRight');
        break;
      case 'fontColor':
        runEditorCommand('foreColor', value || fontColor);
        break;
      case 'fontSans':
        runEditorCommand('fontName', 'Arial');
        break;
      case 'fontSerif':
        runEditorCommand('fontName', 'Georgia');
        break;
      case 'fontMono':
        runEditorCommand('fontName', 'Courier New');
        break;
      case 'fontDefault':
        runEditorCommand('fontName', 'Consolas');
        break;
      case 'link': {
        const href = window.prompt('Enter URL (https://...)', 'https://');
        if (!href) return;
        htmlEditorRef.current.focus();
        document.execCommand('createLink', false, href.trim());
        normalizeEditorLinks();
        syncHtmlFromEditor();
        break;
      }
      case 'clear':
        clearFormatting();
        break;
      default:
        break;
    }
  };

  const handleInsertPlaceholder = (placeholder) => {
    if (!htmlEditorRef.current || saving || generatingNumber) return;
    const token = `{{${String(placeholder || '').trim()}}}`;
    htmlEditorRef.current.focus();
    const didInsert = document.execCommand('insertText', false, token);
    if (!didInsert && window.getSelection) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(token));
        range.collapse(false);
      }
    }
    syncHtmlFromEditor();
  };

  const applyVariableValuesToHtml = (html, valuesByVariable) => {
    let output = String(html || '');
    Object.entries(valuesByVariable || {}).forEach(([key, value]) => {
      const variableName = normalizeVariableName(key);
      if (!variableName) return;
      const pattern = new RegExp(`{{\\s*${escapeRegExp(variableName)}\\s*}}`, 'g');
      output = output.replace(pattern, String(value ?? ''));
    });
    return output;
  };

  const handlePreviewTemplate = () => {
    startPreviewWorkflow({
      number: templateForm.number,
      subject: templateForm.subject,
      htmlContent: templateForm.htmlContent,
      type: templateForm.type,
      variableNames: manualVariables,
    });
  };

  const startPreviewWorkflow = ({ number, subject, htmlContent, type, variableNames = [] }) => {
    const baseSubject = String(subject || '').trim();
    const baseHtml = String(htmlContent || '').trim();
    const normalizedType = String(type || 'static') === 'dynamic' ? 'dynamic' : 'static';
    const normalizedNumber = String(number || '').trim();
    const normalizedVariables = Array.isArray(variableNames)
      ? variableNames.map((item) => normalizeVariableName(item)).filter(Boolean)
      : [];

    setPreviewTemplateNumber(normalizedNumber);
    setPreviewTemplateType(normalizedType);
    setPreviewTemplateVariables(normalizedVariables);

    if (!baseSubject || !baseHtml) {
      showSaveError('Subject and Email Content are required to preview the message.');
      return;
    }

    if (normalizedType === 'dynamic') {
      const definedVariables = normalizedVariables;
      if (definedVariables.length === 0) {
        showSaveError('Define variables before previewing a dynamic template.');
        return;
      }

      const initialValues = definedVariables.reduce((acc, variableName) => {
        acc[variableName] = previewVariableValues[variableName] || '';
        return acc;
      }, {});
      setPreviewSourceSubject(baseSubject);
      setPreviewSourceHtml(baseHtml);
      setPreviewVariableNames(definedVariables);
      setPreviewVariableValues(initialValues);
      setShowPreviewValuesModal(true);
      return;
    }

    setPreviewSourceSubject(baseSubject);
    setPreviewSourceHtml(baseHtml);
    setPreviewSubject(baseSubject);
    setPreviewHtml(baseHtml);
    setShowPreviewModal(true);
  };

  const handlePreviewTemplateFromList = (template) => {
    startPreviewWorkflow({
      number: template?.number || '',
      subject: template?.subject || '',
      htmlContent: template?.htmlContent || '',
      type: template?.type || 'static',
      variableNames: Array.isArray(template?.variables) ? template.variables : [],
    });
  };

  const callTemplateEmailFunction = async (functionName, payload) => {
    const projectUrl = String(import.meta.env.VITE_FIREBASE_PROJECT_URL || '').trim();
    if (!projectUrl) {
      throw new Error('Missing VITE_FIREBASE_PROJECT_URL configuration.');
    }

    const response = await fetch(`https://${functionName}-${projectUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    let responseBody = null;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = null;
    }

    if (!response.ok) {
      const message = String(responseBody?.error || responseBody?.message || response.statusText || 'Failed to send test email.');
      const error = new Error(message);
      error.httpStatus = response.status;
      throw error;
    }

    return responseBody;
  };

  const handleSendTestEmail = async () => {
    if (sendingTestEmail) return;

    const templateNumber = String(previewTemplateNumber || templateForm.number || '').trim();
    if (!templateNumber) {
      showSaveError('Template number is required before sending a test email. Save the template first if needed.');
      return;
    }

    const promptedEmails = window.prompt('Send test email to (comma or semicolon separated):', currentUser?.email || '');
    if (promptedEmails === null) return;

    const toEmails = parseRecipientEmailsInput(promptedEmails);
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = toEmails.filter((email) => !emailPattern.test(email));
    if (toEmails.length === 0) {
      showSaveError('Enter at least one email address for test send.');
      return;
    }
    if (invalidEmails.length > 0) {
      showSaveError(`Invalid email address(es): ${invalidEmails.join(', ')}`);
      return;
    }

    const variableValues = previewTemplateType === 'dynamic'
      ? previewTemplateVariables.reduce((acc, variableName) => {
          acc[variableName] = String(previewVariableValues[variableName] || '');
          return acc;
        }, {})
      : {};

    const combinedToEmail = toEmails.join(', ');

    const payload = {
      senderUserId: currentUser?.uid || '',
      senderName: currentUserName,
      submissionTime: new Date().toISOString(),
      templateNumber,
      totalCount: 1,
      recipients: [
        {
          toEmail: combinedToEmail,
          toEmails,
          recipientEmailCount: toEmails.length,
          recipientType: 'test',
        ...variableValues,
        },
      ],
    };

    setSendingTestEmail(true);
    setError('');
    setSuccess('');

    try {
      try {
        await callTemplateEmailFunction('sendTemplateEmails', payload);
      } catch (err) {
        if (Number(err?.httpStatus) !== 404) {
          throw err;
        }
        await callTemplateEmailFunction('sendTemplateEmail', payload);
      }
      const recipientSummary = toEmails.length === 1 ? toEmails[0] : `1 recipient (${toEmails.length} email addresses)`;
      setSuccess(`Test email sent to ${recipientSummary}.`);
      window.alert(`Test email sent to ${recipientSummary}.`);
    } catch (err) {
      const message = String(err?.message || 'Failed to send test email.');
      showSaveError(message);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    const templateId = String(template?.id || template?.docId || '').trim();
    if (!templateId) return;

    const confirmed = window.confirm(`Delete template ${template?.number || templateId}? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setError('');
      await deleteDoc(doc(db, TEMPLATE_COLLECTION, templateId));
      setSuccess('Email template deleted successfully.');
      await fetchTemplates();
    } catch (err) {
      const code = String(err?.code || '').toLowerCase();
      const message = String(err?.message || 'Failed to delete email template.');
      const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
      showSaveError(isPermissionError
        ? 'Missing or insufficient permissions while deleting email templates. Ensure Firestore rules allow Admin/SystemAdmin/EventAdmin users to delete this collection.'
        : message);
    }
  };

  const handleDuplicateTemplate = async (template) => {
    const clonedVariables = Array.isArray(template?.variables) ? template.variables : [];
    const baseName = String(template?.name || '').trim();
    const duplicateName = baseName ? `${baseName} (Copy)` : 'Template Copy';
    const baseForm = {
      ...createEmptyForm(),
      number: '',
      name: duplicateName,
      fromEmail: String(template?.fromEmail || 'no_reply@icnacon.org'),
      batchSize: String(template?.batchSize || DEFAULT_BATCH_SIZE),
      subject: String(template?.subject || ''),
      type: String(template?.type || 'dynamic'),
      htmlContent: String(template?.htmlContent || ''),
      variablesText: buildVariableText(clonedVariables),
    };

    setEditingTemplate(null);
    setError('');
    setSuccess('');
    setShowTemplateModal(true);
    setTemplateForm(baseForm);
    setSavedFormSignature('');

    setGeneratingNumber(true);
    try {
      const nextTemplateNumber = await reserveNextTemplateNumber();
      const nextForm = { ...baseForm, number: nextTemplateNumber };
      setTemplateForm(nextForm);
      setSavedFormSignature(serializeTemplateForm(nextForm));
    } catch (err) {
      const code = String(err?.code || '').toLowerCase();
      const message = String(err?.message || 'Failed to generate template number.');
      const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
      showSaveError(isPermissionError
        ? 'Missing or insufficient permissions while duplicating template number. Ensure Firestore rules allow Admin/SystemAdmin/EventAdmin users to read/write counters/emailTemplates.'
        : message);
    } finally {
      setGeneratingNumber(false);
    }
  };

  const handlePreviewValueChange = (variableName, value) => {
    setPreviewVariableValues((prev) => ({
      ...prev,
      [variableName]: value,
    }));
  };

  const handlePreviewWithValues = () => {
    const sourceSubject = String(previewSourceSubject || templateForm.subject || previewSubject || '').trim();
    const sourceHtml = String(previewSourceHtml || templateForm.htmlContent || previewHtml || '').trim();
    const renderedSubject = applyVariableValuesToHtml(sourceSubject, previewVariableValues);
    const renderedHtml = applyVariableValuesToHtml(sourceHtml, previewVariableValues);
    setShowPreviewValuesModal(false);
    setPreviewSubject(renderedSubject);
    setPreviewHtml(renderedHtml);
    setShowPreviewModal(true);
  };

  const showSaveError = (message) => {
    const text = String(message || 'Failed to save email template.');
    setError(text);
    window.alert(text);
  };

  const handleSaveTemplate = async () => {
    let trimmedNumber = String(templateForm.number || '').trim();
    const trimmedName = String(templateForm.name || '').trim();
    const trimmedFromEmail = String(templateForm.fromEmail || '').trim();
    const trimmedBatchSize = String(templateForm.batchSize || '').trim();
    const trimmedSubject = String(templateForm.subject || '').trim();
    const trimmedHtml = String(templateForm.htmlContent || '').trim();
    const normalizedType = templateForm.type === 'dynamic' ? 'dynamic' : 'static';
    const usedVariables = normalizedType === 'dynamic' ? detectedVariables : [];
    const definedVariables = normalizedType === 'dynamic' ? manualVariables : [];
    const variables = normalizedType === 'dynamic' ? usedVariables : [];
    const parsedBatchSize = parsePositiveInteger(trimmedBatchSize);

    if (!editingTemplate && !trimmedNumber) {
      setGeneratingNumber(true);
      try {
        trimmedNumber = await reserveNextTemplateNumber();
        setTemplateForm((prev) => ({ ...prev, number: trimmedNumber }));
      } catch (err) {
        const code = String(err?.code || '').toLowerCase();
        const message = String(err?.message || 'Failed to generate template number.');
        const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
        showSaveError(isPermissionError
          ? 'Missing or insufficient permissions while generating template number. Ensure Firestore rules allow Admin/SystemAdmin/EventAdmin users to read/write counters/emailTemplates.'
          : message);
        setGeneratingNumber(false);
        return;
      }
      setGeneratingNumber(false);
    }

    const duplicateTemplate = templates.find((item) => {
      const itemNumber = String(item?.number || '').trim();
      const itemId = String(item?.id || item?.docId || '').trim();
      const currentFormId = String(templateForm.id || '').trim();
      return itemNumber && itemNumber === trimmedNumber && itemId !== currentFormId;
    });

    setError('');
    setSuccess('');

    if (!trimmedNumber || !trimmedName || !trimmedFromEmail || !trimmedBatchSize || !trimmedSubject || !trimmedHtml) {
      showSaveError('Template number, name, from email, batch size, subject, and Email Content are required.');
      return;
    }
    if (!parsedBatchSize) {
      showSaveError('Batch Size must be a positive whole number.');
      return;
    }
    if (duplicateTemplate) {
      showSaveError('Template number must be unique.');
      return;
    }
    if (normalizedType === 'static' && (detectedVariablesInContent.length > 0 || detectedVariablesInSubject.length > 0)) {
      showSaveError('Static templates cannot contain {{variable}} placeholders in subject or content. Remove placeholders or switch the template type to dynamic.');
      return;
    }
    if (normalizedType === 'dynamic' && usedVariables.length === 0) {
      showSaveError('Dynamic templates must use at least one {{variable}} placeholder in subject or Email Content.');
      return;
    }
    if (normalizedType === 'dynamic' && definedVariables.length === 0) {
      showSaveError('Define at least one variable in the Variables field for dynamic templates.');
      return;
    }
    if (normalizedType === 'dynamic' && !listsMatchAsSet(definedVariables, usedVariables)) {
      showSaveError('Defined Variables must exactly match Used Variables from Email Content before saving.');
      return;
    }

    const payload = {
      number: trimmedNumber,
      name: trimmedName,
      fromEmail: trimmedFromEmail,
      batchSize: parsedBatchSize,
      subject: trimmedSubject,
      type: normalizedType,
      htmlContent: trimmedHtml,
      variables,
      updatedBy: currentUser?.uid || '',
      updatedByName: currentUserName,
      updatedAt: serverTimestamp(),
    };

    setSaving(true);
    try {
      if (editingTemplate && (editingTemplate.id || editingTemplate.docId)) {
        const templateId = String(editingTemplate.id || editingTemplate.docId);
        await updateDoc(doc(db, TEMPLATE_COLLECTION, templateId), payload);
        const nextForm = {
          ...templateForm,
          number: trimmedNumber,
          name: trimmedName,
          fromEmail: trimmedFromEmail,
          batchSize: String(parsedBatchSize),
          subject: trimmedSubject,
          type: normalizedType,
          htmlContent: trimmedHtml,
          variablesText: buildVariableText(variables),
        };
        setTemplateForm(nextForm);
        setSavedFormSignature(serializeTemplateForm(nextForm));
        setSuccess('Email template updated successfully.');
      } else {
        const templateRef = doc(collection(db, TEMPLATE_COLLECTION));
        await setDoc(templateRef, {
          id: templateRef.id,
          ...payload,
          createdBy: currentUser?.uid || '',
          createdByName: currentUserName,
          createdAt: serverTimestamp(),
        });
        const nextForm = {
          id: templateRef.id,
          number: trimmedNumber,
          name: trimmedName,
          fromEmail: trimmedFromEmail,
          batchSize: String(parsedBatchSize),
          subject: trimmedSubject,
          type: normalizedType,
          htmlContent: trimmedHtml,
          variablesText: buildVariableText(variables),
        };
        setEditingTemplate({ id: templateRef.id, docId: templateRef.id });
        setTemplateForm(nextForm);
        setSavedFormSignature(serializeTemplateForm(nextForm));
        setSuccess('Email template created successfully.');
      }
      await fetchTemplates();
      return true;
    } catch (err) {
      const code = String(err?.code || '').toLowerCase();
      const message = String(err?.message || 'Failed to save email template.');
      const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
      showSaveError(isPermissionError
        ? 'Missing or insufficient permissions while saving email templates. Ensure Firestore rules allow Admin/SystemAdmin/EventAdmin users to write this collection.'
        : message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="home-container">
      <div className="user-card">
        <div className="home-header" style={{ marginBottom: '1rem' }}>
          <h2 className="profile-title" style={{ margin: 0 }}>Admin: Email Templates</h2>
          <div className="admin-actions">
            <button type="button" className="back-btn" onClick={() => navigate('/')}>Back</button>
            <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
            <button type="button" className="open-btn" onClick={() => navigate('/admin/email-template-logs')}>Email Logs</button>
            <button type="button" className="new-layout-btn" onClick={openCreateTemplateModal}>Create Template</button>
          </div>
        </div>

        <div className="summary-note" style={{ marginBottom: 12 }}>
          Static templates can be reused for bulk sends. Dynamic templates use placeholders like {'{{firstName}}'} and are personalized per recipient.
        </div>

        {error && <div className="error-state" style={{ marginBottom: 12 }}><h4>{error}</h4></div>}
        {success && <div style={{ marginBottom: 12, color: '#155724', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 8, padding: 10 }}>{success}</div>}

        <div className="layouts-table-wrapper">
          <div>
            <table className="events-table layouts-table" style={{ minWidth: 1200 }}>
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>Number</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                  <th>Updated By</th>
                  <th>Updated Date</th>
                </tr>
              </thead>
              <tbody>
                {templates.length > 0 ? templates.map((template) => (
                  <tr key={template.docId}>
                    <td>
                      <div className="icon-actions">
                        <button type="button" className="icon-btn edit" aria-label="Edit" data-tooltip="Edit" onClick={() => openEditTemplateModal(template)}>✏️</button>
                        <button type="button" className="icon-btn duplicate" aria-label="Duplicate" data-tooltip="Duplicate" onClick={() => handleDuplicateTemplate(template)}>📄</button>
                        <button type="button" className="icon-btn" aria-label="Preview" data-tooltip="Preview" onClick={() => handlePreviewTemplateFromList(template)}>👁️</button>
                        <button type="button" className="icon-btn delete" aria-label="Delete" data-tooltip="Delete" onClick={() => handleDeleteTemplate(template)}>🗑️</button>
                      </div>
                    </td>
                    <td>{template.number || '-'}</td>
                    <td>
                      <div>{template.name || '-'}</div>
                      <small style={{ color: '#666', display: 'block' }}>Subject: {template.subject || '-'}</small>
                      {template.type === 'dynamic' && Array.isArray(template.variables) && template.variables.length > 0 && (
                        <small style={{ color: '#666' }}>Variables: {template.variables.join(', ')}</small>
                      )}
                    </td>
                    <td>{template.type || '-'}</td>
                    <td>{template.createdByName || template.createdBy || '-'}</td>
                    <td>{formatDateTime(template.createdAt)}</td>
                    <td>{template.updatedByName || template.updatedBy || '-'}</td>
                    <td>{formatDateTime(template.updatedAt)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8}>{loading ? 'Loading email templates...' : 'No email templates found.'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showTemplateModal && (
        <div className="email-template-modal-backdrop" role="dialog" aria-modal="true">
          <div className="email-template-modal-card">
            <div className="email-template-modal-header">
              <h3 style={{ margin: 0 }}>{editingTemplate ? 'Edit Email Template' : 'Create Email Template'}</h3>
              <div className="form-actions" style={{ marginTop: 0 }}>
                <button type="button" className="open-btn" onClick={handlePreviewTemplate} disabled={saving || generatingNumber}>Preview</button>
                <button type="button" className="create-btn" onClick={handleSaveTemplate} disabled={saving || generatingNumber || !templateForm.number}>{saving ? 'Saving...' : (generatingNumber ? 'Generating Number...' : 'Save')}</button>
                <button type="button" className="cancel-btn" onClick={closeTemplateModal} disabled={saving || generatingNumber}>Close</button>
              </div>
            </div>

            {error && <div className="error-state" style={{ marginBottom: 12 }}><h4>{error}</h4></div>}

            <div className="email-template-grid email-template-grid-top">
              <div>
                <label>Number</label>
                <input
                  className="layout-name-input compact-field compact-field-xs number-field"
                  type="text"
                  value={templateForm.number}
                  readOnly
                  disabled={saving || generatingNumber}
                />
              </div>
              <div>
                <label>Type</label>
                <select
                  className="layout-name-input compact-field compact-field-xs type-field"
                  value={templateForm.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  disabled={saving || generatingNumber}
                >
                  <option value="static">static</option>
                  <option value="dynamic">dynamic</option>
                </select>
              </div>
              <div>
                <label>Template Name</label>
                <input
                  ref={templateNameInputRef}
                  className="layout-name-input"
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  disabled={saving || generatingNumber}
                />
              </div>
              <div>
                <label>From Email</label>
                <input
                  className="layout-name-input from-email-field"
                  type="email"
                  value={templateForm.fromEmail}
                  onChange={(e) => handleFormChange('fromEmail', e.target.value)}
                  disabled={saving || generatingNumber}
                  placeholder="no_reply@icnacon.org"
                />
              </div>
            </div>

            <div style={{ marginTop: 8 }}>
              <div className="email-template-subject-header">
                <label>Subject</label>
                <div className="email-template-subject-meta">
                  <span className="email-template-subject-count">{String(templateForm.subject || '').length}/{SUBJECT_MAX_LENGTH}</span>
                </div>
              </div>
              <div className="email-template-subject-row">
                <input
                  className="layout-subject-input"
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => handleFormChange('subject', e.target.value)}
                  disabled={saving || generatingNumber}
                  maxLength={SUBJECT_MAX_LENGTH}
                  placeholder="Use placeholders like {{firstName}} if needed"
                />
                <div className="email-template-batch-inline">
                  <label>Batch Size</label>
                  <input
                    className="layout-name-input compact-field compact-field-xs batch-size-field"
                    type="number"
                    min="1"
                    step="1"
                    value={templateForm.batchSize}
                    onChange={(e) => handleFormChange('batchSize', e.target.value)}
                    disabled={saving || generatingNumber}
                  />
                </div>
              </div>
            </div>

            <div className="email-template-editors">
              <div>
                <label>Email Content</label>
                <div className="email-template-toolbar">
                  <button type="button" className={`email-template-toolbar-btn ${activeFormats.bold ? 'active' : ''}`} onClick={() => handleApplyHtmlFormat('bold')} disabled={saving || generatingNumber} title="Bold">B</button>
                  <button type="button" className={`email-template-toolbar-btn ${activeFormats.italic ? 'active' : ''}`} onClick={() => handleApplyHtmlFormat('italic')} disabled={saving || generatingNumber} title="Italic"><em>I</em></button>
                  <button type="button" className={`email-template-toolbar-btn ${activeFormats.underline ? 'active' : ''}`} onClick={() => handleApplyHtmlFormat('underline')} disabled={saving || generatingNumber} title="Underline"><u>U</u></button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('alignLeft')} disabled={saving || generatingNumber} title="Align Left">L</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('alignCenter')} disabled={saving || generatingNumber} title="Align Center">C</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('alignRight')} disabled={saving || generatingNumber} title="Align Right">R</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('fontSans')} disabled={saving || generatingNumber} title="Sans Font">Sans</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('fontSerif')} disabled={saving || generatingNumber} title="Serif Font">Serif</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('fontMono')} disabled={saving || generatingNumber} title="Monospace Font">Mono</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('fontDefault')} disabled={saving || generatingNumber} title="Default Font">Default</button>
                  <input
                    type="color"
                    className="email-template-toolbar-color"
                    aria-label="Font Color"
                    title="Font Color"
                    value={fontColor}
                    disabled={saving || generatingNumber}
                    onChange={(e) => {
                      setFontColor(e.target.value);
                      handleApplyHtmlFormat('fontColor', e.target.value);
                    }}
                  />
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('sizeUp')} disabled={saving || generatingNumber} title="Increase Text Size">A+</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('sizeDown')} disabled={saving || generatingNumber} title="Decrease Text Size">A-</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('paragraph')} disabled={saving || generatingNumber} title="New Paragraph">P</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('ul')} disabled={saving || generatingNumber} title="Bulleted List">•</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('ol')} disabled={saving || generatingNumber} title="Numbered List">1.</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('link')} disabled={saving || generatingNumber} title="Insert Link">Link</button>
                  <button type="button" className="email-template-toolbar-btn" onClick={() => handleApplyHtmlFormat('clear')} disabled={saving || generatingNumber} title="Clear Formatting">Clear</button>
                </div>
                <div className="email-template-placeholders">
                  {placeholderChips.length > 0 ? placeholderChips.map((placeholder) => (
                    <button
                      key={placeholder}
                      type="button"
                      className="email-template-placeholder-chip"
                      onClick={() => handleInsertPlaceholder(placeholder)}
                      disabled={saving || generatingNumber}
                      title={`Insert {{${placeholder}}}`}
                    >
                      {`{{${placeholder}}}`}
                    </button>
                  )) : (
                    <div className="summary-note" style={{ margin: 0 }}>
                      Define variables to see quick insert chips.
                    </div>
                  )}
                </div>
                <div
                  ref={htmlEditorRef}
                  className="email-template-editor"
                  contentEditable={!saving && !generatingNumber}
                  suppressContentEditableWarning
                  data-placeholder="Write your email here and use toolbar formatting."
                  onInput={syncHtmlFromEditor}
                  onKeyDown={handleEditorKeyDown}
                />
              </div>
              <div>
                <label>Variables</label>
                <textarea
                  className="email-template-variable-input"
                  placeholder={'firstName\nlastName\ninvoiceNumber'}
                  value={templateForm.variablesText}
                  onChange={(e) => handleFormChange('variablesText', e.target.value)}
                  disabled={saving || generatingNumber || templateForm.type === 'static'}
                />
                <div className="summary-note" style={{ marginTop: 8 }}>
                  One variable per line or comma-separated. Dynamic templates require Variables to exactly match placeholders used in subject and Email Content.
                </div>

                <div style={{ marginTop: 14 }}>
                  <label>Used Variables</label>
                  <div className="email-template-variable-box">
                    {detectedVariables.length > 0 ? detectedVariables.map((v) => (
                      <span key={v} className="email-template-variable-chip">{v}</span>
                    )) : <div style={{ color: '#777' }}>No variables defined.</div>}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <label style={{ margin: 0 }}>HTML Code</label>
                <button
                  type="button"
                  className="email-template-toolbar-btn"
                  onClick={() => setIsEditingHtmlCode((prev) => !prev)}
                  disabled={saving || generatingNumber}
                  title="Edit HTML"
                >
                  {isEditingHtmlCode ? 'Done' : 'Edit HTML'}
                </button>
              </div>
              {isEditingHtmlCode ? (
                <textarea
                  className="email-template-html-code"
                  value={templateForm.htmlContent}
                  onChange={(e) => handleFormChange('htmlContent', e.target.value)}
                  disabled={saving || generatingNumber}
                />
              ) : (
                <pre className="email-template-html-code">{templateForm.htmlContent || '<p>Your formatted content will appear as HTML here.</p>'}</pre>
              )}
            </div>

          </div>
        </div>
      )}

      {showPreviewModal && (
        <div className="email-template-modal-backdrop email-template-modal-backdrop-top" role="dialog" aria-modal="true">
          <div className="email-template-preview-modal-card">
            <div className="email-template-modal-header">
              <h3 style={{ margin: 0 }}>Message Preview</h3>
              <div className="form-actions" style={{ marginTop: 0 }}>
                <button type="button" className="create-btn" onClick={handleSendTestEmail} disabled={sendingTestEmail}>{sendingTestEmail ? 'Sending...' : 'Send Test Email'}</button>
                <button type="button" className="cancel-btn" onClick={() => setShowPreviewModal(false)}>Close</button>
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ marginBottom: 4 }}>Subject</label>
              <div className="email-template-preview" style={{ minHeight: 48 }}>{previewSubject || '-'}</div>
            </div>
            <div
              className="email-template-preview"
              dangerouslySetInnerHTML={{ __html: previewHtml || '<p style="color:#888;margin:0;">No preview content available.</p>' }}
            />
          </div>
        </div>
      )}

      {showPreviewValuesModal && (
        <div className="email-template-modal-backdrop email-template-modal-backdrop-top" role="dialog" aria-modal="true">
          <div className="email-template-preview-modal-card">
            <div className="email-template-modal-header">
              <h3 style={{ margin: 0 }}>Preview Values</h3>
              <div className="form-actions" style={{ marginTop: 0 }}>
                <button type="button" className="create-btn" onClick={handlePreviewWithValues}>Show Preview</button>
                <button type="button" className="cancel-btn" onClick={() => { setShowPreviewValuesModal(false); setPreviewSourceSubject(''); setPreviewSourceHtml(''); }}>Cancel</button>
              </div>
            </div>

            <div className="email-template-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              {previewVariableNames.map((variableName) => (
                <div key={variableName}>
                  <label>{variableName}</label>
                  <input
                    className="layout-name-input"
                    type="text"
                    autoFocus={variableName === previewVariableNames[0]}
                    value={previewVariableValues[variableName] || ''}
                    onChange={(e) => handlePreviewValueChange(variableName, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesAdmin;